import {Link} from "react-router-dom";
import {useContext, useEffect, useState} from "react";
import ProductItemList from "./ProductItemList";
import ProductGroupItemList from "./ProductGroupItemList";
import Cart from "./Cart";
import {useQuery} from "@tanstack/react-query";
import {getCart, getOrderSettingsInfo, searchCustomers} from "../../api/api";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import {toast} from "react-toastify";
import { AsyncPaginate } from "react-select-async-paginate";
import AuthContext from "../../context/AuthContext";

function CartPageNew() {

    const { refreshToken } = useContext(AuthContext);

    const [currentTab, setCurrentTab] = useState(1)
    const [itemComponent, setItemComponent] = useState(<ProductItemList currentTab={currentTab} setCurrentTab={setCurrentTab}/>)

    const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
    const [customer, setCustomer] = useState('');

    const [queryKey, setQueryKey] = useState(['cart', customer]);

    useEffect(() => {
        const saved = localStorage.getItem('selectedCustomer');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setSelectedCustomerOption(parsed);
                setCustomer(parsed.value);
                setQueryKey(['cart', parsed.value]);
                if (currentTab === 1) {
                    setItemComponent(<ProductItemList currentTab={currentTab} setCurrentTab={setCurrentTab} customer={parsed.value} />);
                } else if (currentTab === 2) {
                    setItemComponent(<ProductGroupItemList currentTab={currentTab} setCurrentTab={setCurrentTab} customer={parsed.value} />);
                }
            } catch {
                localStorage.removeItem('selectedCustomer');
            }
        }
    }, [currentTab]);

    const { data: orderSettingsData } = useQuery({
        queryKey: ['order-settings'],
        queryFn: getOrderSettingsInfo,
        retry: false,
        staleTime: 5 * 60 * 1000,
    });
    const defaultPaymentMethod = orderSettingsData?.results?.[0]?.default_payment_method ?? 'CASH';
    const autoPrintShortBill = orderSettingsData?.results?.[0]?.auto_print_short_bill ?? false;

    const { status, data, refetch } = useQuery({
        queryKey: queryKey,
        queryFn: () => getCart(1, '', customer, '', '', '', '',
            '', '', '', '', ''),
        keepPreviousData: true,
        retry: false,
        enabled: !!customer,
        onError: (err) => {
            handleUnauthorized401QueryError(err, refreshToken, refetch);
            if (err.response.status !== 401) {
                toast.error(`An error occurred! ${err?.response?.data?.detail}`);
            }
        }
    });

    const loadOptions = async (inputValue, _, { page }) => {
        const data = await searchCustomers(page, inputValue, 10);
        return {
            options: data.results.map(c => {
                // Customers default to a bare "+91" when no number was entered
                const phone = c.phone && c.phone !== '+91' ? c.phone : null;
                return {
                    value: c.email,
                    label: [c.name, phone, c.email].filter(Boolean).join(' · '),
                };
            }),
            hasMore: !!data.next,
            additional: { page: page + 1 },
        };
    };

    const handleSelect = (selectedOption, userAction) => {
        const {action} = userAction
        if(action === 'clear') {
            setSelectedCustomerOption(null);
            setCustomer('');
            localStorage.removeItem('selectedCustomer');
            if (currentTab !== 1) {
                setCurrentTab(1);
            }
            setItemComponent(<ProductItemList currentTab={currentTab} setCurrentTab={setCurrentTab} customer={''}/>)
            setQueryKey(['cart', '']);
        }
        if(action === 'select-option') {
            setSelectedCustomerOption(selectedOption);
            setCustomer(selectedOption.value);
            localStorage.setItem('selectedCustomer', JSON.stringify(selectedOption));
            if (currentTab !== 1) {
                setCurrentTab(1);
            }
            setItemComponent(<ProductItemList currentTab={currentTab} setCurrentTab={setCurrentTab} customer={selectedOption.value}/>)
            setQueryKey(['cart', selectedOption.value]);
        }
    }

    const handleTabChange = (e, tab) => {
        switch(tab) {
            case 1:
                setCurrentTab(1)
                setItemComponent(<ProductItemList currentTab={currentTab} setCurrentTab={setCurrentTab} customer={customer}/>)
                break
            case 2:
                setCurrentTab(2)
                setItemComponent(<ProductGroupItemList currentTab={currentTab} setCurrentTab={setCurrentTab} customer={customer}/>)
                break
            default:
                setItemComponent(<ProductItemList currentTab={1} setCurrentTab={setCurrentTab} customer={customer}/>)
        }
    };

    return (
        <>

            <div className="row">
                <div className="col-12">
                    <div className="form-group d-flex justify-content-between">
                        <AsyncPaginate
                            id="customer-select"
                            className="search-field"
                            loadOptions={loadOptions}
                            additional={{ page: 1 }}
                            onChange={handleSelect}
                            placeholder="Search customer by name, phone or email"
                            // The API already matched name/email/phone (with phone formatting
                            // normalised); don't let react-select re-filter results by label
                            filterOption={null}
                            isSearchable
                            isClearable
                            value={selectedCustomerOption}
                            debounceTimeout={300}
                            styles={{
                                input: (provided) => ({
                                    ...provided,
                                    color: 'white',
                                }),
                                placeholder: (provided) => ({
                                    ...provided,
                                    color: '#a8b5c3',
                                }),
                                menu: (provided) => ({
                                    ...provided,
                                    backgroundColor: '#394450',
                                    zIndex: 999,
                                }),
                                menuList: (provided) => ({
                                    ...provided,
                                    color: '#ffffff',
                                }),
                                option: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: state.isFocused ? '#4d5bed' : 'transparent',

                                }),
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-lg-12">
                    <div className="card-box">
                        <ul className="nav nav-tabs nav-bordered">
                            <li className="nav-item">
                                <Link onClick={(e) => handleTabChange(e, 1)} data-bs-toggle="tab" aria-expanded="true" className={`nav-link ${currentTab===1? 'active': null}`}>
                                <span className="d-inline-block d-sm-none"><i
                                    className="mdi mdi-account"/></span>
                                    <span className="d-none d-sm-inline-block">Products</span>
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link onClick={(e) => handleTabChange(e, 2)} data-bs-toggle="tab" aria-expanded="false" className={`nav-link ${currentTab===2? 'active': null}`}>
                                        <span className="d-inline-block d-sm-none"><i
                                            className="mdi mdi-office-building"/></span>
                                    <span className="d-none d-sm-inline-block">Product Groups</span>
                                </Link>
                            </li>
                        </ul>
                        <div className="tab-content">
                            <div className="row">
                                <div className="col-lg-6">
                                    {itemComponent}
                                </div>

                                <div className="col-lg-6">
                                    <Cart customer={customer} status={status} data={data} defaultPaymentMethod={defaultPaymentMethod} autoPrintShortBill={autoPrintShortBill}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default CartPageNew;
